import React, { useState } from 'react';
import { formatCurrency, assetUnitValue, isAppreciatingAsset } from '../gameData';
import AlertDialog from './AlertDialog';

function AssetsMarket({ player, setPlayer, availableAssets, gameEnded }) {
  const [quantities, setQuantities] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const showAlertMessage = (message) => {
    setAlertMessage(message);
    setShowAlert(true);
  };

  const handleQuantityChange = (assetName, value) => {
    setQuantities(prev => ({ ...prev, [assetName]: parseInt(value) }));
  };

  const buyAsset = (assetName) => {
    if (gameEnded) return;

    const asset = availableAssets.find(a => a.name === assetName);
    const quantity = quantities[assetName] || 1;
    const totalCost = asset.price * quantity;

    if (player.money < totalCost) {
      showAlertMessage("Not enough money to buy assets!");
      return;
    }

    setPlayer(prev => {
      const existingAsset = prev.assets.find(a => a.name === assetName);

      // Buying more of an asset blends the cost basis and purchase day by
      // quantity, so appreciation/depreciation tracks a fair average age.
      if (existingAsset) {
        const oldQty = existingAsset.quantity;
        const newQty = oldQty + quantity;
        const newBasePrice = ((existingAsset.basePrice * oldQty) + (asset.price * quantity)) / newQty;
        const newPurchaseDay = ((existingAsset.purchaseDay * oldQty) + (prev.day * quantity)) / newQty;

        return {
          ...prev,
          money: prev.money - totalCost,
          assets: prev.assets.map(a =>
            a.name === assetName
              ? { ...a, quantity: newQty, basePrice: newBasePrice, purchaseDay: newPurchaseDay }
              : a
          )
        };
      }

      return {
        ...prev,
        money: prev.money - totalCost,
        assets: [...prev.assets, {
          name: assetName,
          quantity,
          basePrice: asset.price,
          purchaseDay: prev.day,
        }]
      };
    });
    // Reset slider after purchase
    setQuantities(prev => ({ ...prev, [assetName]: 1 }));
  };

  const liquidateAsset = (assetName) => {
    if (gameEnded) return;

    const quantity = quantities[assetName] || 1;
    const existingAsset = player.assets.find(a => a.name === assetName);

    if (existingAsset && existingAsset.quantity >= quantity) {
      const currentValue = assetUnitValue(existingAsset, player.day);
      const totalValue = currentValue * quantity;

      setPlayer(prev => ({
        ...prev,
        money: prev.money + totalValue,
        assets: prev.assets
          .map(a => a.name === assetName ? 
            { ...a, quantity: a.quantity - quantity } : a)
          .filter(a => a.quantity > 0)
      }));
      // Reset slider after liquidation
      setQuantities(prev => ({ ...prev, [assetName]: 1 }));
    } else {
      showAlertMessage("Not enough assets to liquidate!");
    }
  };

  return (
    <div>
      <h2>Assets Market</h2>
      <table>
        <thead>
          <tr>
            <th>Asset</th>
            <th>Price</th>
            <th>Quantity Owned</th>
            <th>Total Value</th>
            <th>Quantity</th>
            <th>Buy/Liquidate</th>
          </tr>
        </thead>
        <tbody>
          {availableAssets.map(asset => {
            const owned = player.assets.find(a => a.name === asset.name);
            const quantityOwned = owned?.quantity || 0;
            const currentValue = owned ? assetUnitValue(owned, player.day) : asset.price;
            const totalValue = currentValue * quantityOwned;
            const maxBuyable = Math.floor(player.money / asset.price);
            
            const appreciates = isAppreciatingAsset(asset.name);

            return (
              <tr key={asset.name}>
                <td>
                  {asset.name}{' '}
                  <span
                    className={appreciates ? 'asset-tag up' : 'asset-tag down'}
                    title={appreciates ? 'Appreciates while held' : 'Depreciates while held'}
                  >
                    {appreciates ? '▲' : '▼'}
                  </span>
                </td>
                <td>{formatCurrency(asset.price)}</td>
                <td>{quantityOwned}</td>
                <td>{formatCurrency(totalValue)}</td>
                <td>
                  <input
                    type="range"
                    min="1"
                    max={Math.max(1, maxBuyable, quantityOwned)}
                    value={quantities[asset.name] || 1}
                    onChange={(e) => handleQuantityChange(asset.name, e.target.value)}
                    disabled={gameEnded}
                  />
                  <span>{quantities[asset.name] || 1}</span>
                </td>
                <td>
                  <button onClick={() => buyAsset(asset.name)} disabled={gameEnded}>Buy</button>
                  <button onClick={() => liquidateAsset(asset.name)} disabled={gameEnded}>Liquidate</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      
      <AlertDialog
        show={showAlert}
        message={alertMessage}
        onClose={() => setShowAlert(false)}
        type="error"
      />
    </div>
  );
}

export default AssetsMarket; 